import { getMe } from './me'

// What kind of round this is (0028, Liam 2026-09-26: "a cleaning round or a
// gardening round ... a plant watering round"). The app is the same for every
// trade; the trade only chooses the words. Keep in step with
// functions/lib/trade.ts.
export type Trade = 'window_cleaning' | 'cleaning' | 'gardening' | 'plant_care' | 'exterior' | 'other'

export const TRADE_OPTIONS: { value: Trade; label: string; example: string }[] = [
  { value: 'window_cleaning', label: 'Window cleaning', example: 'Sam’s Window Cleaning' },
  { value: 'cleaning', label: 'Cleaning', example: 'Sam’s Cleaning' },
  { value: 'gardening', label: 'Gardening', example: 'Sam’s Gardens' },
  { value: 'plant_care', label: 'Plant watering', example: 'Sam’s Plant Care' },
  // Patio, roof and driveway cleaning (Liam picked A, 15 Oct 2026: "Would it work for this patio and roof cleaning business?").
  { value: 'exterior', label: 'Patio, roof and driveway', example: 'Sam’s Exterior Cleaning' },
  { value: 'other', label: 'Something else', example: 'Sam’s Services' },
]

// The three job types a visit can be (cleans.kind), in this trade's words.
const KIND_WORDS: Record<Trade, Record<'window' | 'gutter' | 'other', string>> = {
  window_cleaning: { window: 'Windows', gutter: 'Gutters', other: 'Other service' },
  cleaning: { window: 'Clean', gutter: 'Deep clean', other: 'Other service' },
  gardening: { window: 'Garden visit', gutter: 'Hedges and trees', other: 'Other service' },
  plant_care: { window: 'Watering', gutter: 'Plant care', other: 'Other service' },
  exterior: { window: 'Patio and driveway', gutter: 'Roof and gutters', other: 'Other service' },
  other: { window: 'Visit', gutter: 'Extra job', other: 'Other service' },
}

export function currentTrade(): Trade {
  const t = getMe()?.trade
  return t && t in KIND_WORDS ? (t as Trade) : 'window_cleaning'
}

// The work itself in this trade's words, for what is written to a customer or the owner: a worker's invoice line, an
// enquiry or quote email's subject (Liam's notes, 15 Oct 2026: a gardening round still read "window cleaning"). 'other'
// has no word of its own, so its wording stays plain ("Work", "Your enquiry"). Keep in step with functions/lib/trade.ts.
const WORK_WORD: Record<Trade, string | null> = {
  window_cleaning: 'window cleaning',
  cleaning: 'cleaning',
  gardening: 'gardening',
  plant_care: 'plant watering',
  exterior: 'exterior cleaning',
  other: null,
}

// "Window cleaning" / "Plant watering" / "Work": the start of a line, before the day.
export function workTitle(trade: Trade = currentTrade()): string {
  const w = WORK_WORD[trade]
  return w ? `${w[0]!.toUpperCase()}${w.slice(1)}` : 'Work'
}

// The subject of an email to someone who asked about the work: "Your window cleaning enquiry", "Your quote".
export function mailSubject(kind: 'enquiry' | 'quote' | 'reply', trade: Trade = currentTrade()): string {
  const w = WORK_WORD[trade]
  const of = w ? `${w} ` : ''
  return kind === 'reply' ? `Re: your ${of}enquiry` : `Your ${of}${kind}`
}

export function kindLabel(kind: string | null | undefined): string {
  const words = KIND_WORDS[currentTrade()]
  return kind === 'gutter' ? words.gutter : kind === 'other' ? words.other : words.window
}
